/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useRef } from 'react';
import type { SkuDetails } from '../../../interface';
import { NavBar } from '@/components/NavBar';
import { useDealDrawerFulfillmentSelection } from '../../../api/getDealDrawerFulfillmentSelection';
import { DealsLoader } from '../DealsLoader';
import { getErrorCode, getErrorCodeDescription, getErrorMessage } from '@/utils/errorsHandling';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import NotificationBlock from '@/components/AZCustomComponent/NotificationBlock/NotificationBlockV2';
import { useExecuteOnce } from '@/hooks/useExecuteOnce';
import styles from './styles.module.scss';

type Props = {
  parentSkuDetails: SkuDetails;
  dealId: string;
  preferredVehicleId: string | undefined;
  onSuccess: (groupTypeId: string, fulfillmentId?: number) => void;
  onFailure: () => void;
  onClose: () => void;
  onBack: () => void;
  handleErrorAnalytics: (a: string, b: string) => void;
};

const prioritizedOnlineMethods = [
  FULFILLMENT_METHODS.NEXTDAY,
  FULFILLMENT_METHODS.SAMEDAY,
  FULFILLMENT_METHODS.VDPDS,
  FULFILLMENT_METHODS.VDP,
  FULFILLMENT_METHODS.FREIGHT,
  FULFILLMENT_METHODS.CUSTOMIZEDSKU,
  FULFILLMENT_METHODS.ONLINEORDER,
];

const prioritizedMethodsSet = new Set(prioritizedOnlineMethods);

type FulfillmentOption = {
  available: boolean;
  fulfillmentTypeId: number;
  groupTypeId: number;
};

const selectBestFulfillmentOption = (
  fullFulfillmentOptions: Record<string, FulfillmentOption>,
): FulfillmentOption | null => {
  const options = Object.values(fullFulfillmentOptions);
  const availableOptions = options.filter((option) => option.available);

  if (availableOptions.length === 0) {
    return null;
  }

  const storePickupOption = availableOptions.find(
    (option) => option.fulfillmentTypeId === FULFILLMENT_METHODS.STOREORDER,
  );

  if (storePickupOption) {
    return storePickupOption;
  }

  const onlineOptions = availableOptions.filter(
    (option) => option.groupTypeId === FULFILLMENT_METHODS.ONLINEORDER,
  );

  const bestOption = onlineOptions.find((option) =>
    prioritizedMethodsSet.has(option.fulfillmentTypeId),
  );

  return bestOption || null;
};

export const AutoFulfillmentSelector = ({
  parentSkuDetails,
  dealId,
  preferredVehicleId,
  onSuccess,
  onFailure,
  onClose,
  onBack,
  handleErrorAnalytics,
}: Props) => {
  const { id: skuId } = parentSkuDetails;

  const fulfillmentSelectionQuery = useDealDrawerFulfillmentSelection({
    skuId,
    dealId,
    selectedVehicle: preferredVehicleId,
  });

  /**
   * Uses useExecuteOnce with ref guards to prevent duplicate executions when callbacks (onSuccess, onFailure)
   * change references due to parent re-renders. The guard resets when new data arrives, allowing fresh selection for each query result.
   * Variables inside fn() don't need memoization since the function executes only once per data cycle, eliminating re-computation overhead.
   */
  const hasAutoSelectedRef = useRef(false);
  const lastDataRef = useRef(fulfillmentSelectionQuery.data);

  const errorHandledRef = useRef(false);
  const lastErrorRef = useRef(fulfillmentSelectionQuery.error);

  if (lastDataRef.current !== fulfillmentSelectionQuery.data) {
    hasAutoSelectedRef.current = false;
    lastDataRef.current = fulfillmentSelectionQuery.data;
  }

  if (lastErrorRef.current !== fulfillmentSelectionQuery.error) {
    errorHandledRef.current = false;
    lastErrorRef.current = fulfillmentSelectionQuery.error;
  }

  if (!fulfillmentSelectionQuery.isError) {
    errorHandledRef.current = false;
  }

  useExecuteOnce({
    enabled:
      !!fulfillmentSelectionQuery.data?.fullFulfillmentOptions && !hasAutoSelectedRef.current,
    fn: () => {
      const { fullFulfillmentOptions } = fulfillmentSelectionQuery.data!;

      hasAutoSelectedRef.current = true;

      const selectedOption = selectBestFulfillmentOption(fullFulfillmentOptions);

      if (selectedOption) {
        onSuccess(`${selectedOption.groupTypeId}`, selectedOption.fulfillmentTypeId);
      } else {
        onFailure();
      }
    },
  });

  useExecuteOnce({
    enabled: fulfillmentSelectionQuery.isError && !errorHandledRef.current,
    fn: () => {
      errorHandledRef.current = true;
      handleErrorAnalytics(
        getErrorMessage(fulfillmentSelectionQuery.error) ?? '',
        getErrorCode(fulfillmentSelectionQuery.error) ?? '',
      );
      onFailure();
    },
  });

  if (fulfillmentSelectionQuery.isError) {
    return (
      <>
        <NavBar showBackButton showCloseButton handleBack={onBack} handleClose={onClose} />
        <div className={styles.autoFulfillmentContainer} data-testid="auto-fulfillment-selector">
          <div className={styles.errorContainer}>
            <NotificationBlock
              type="error"
              message={getErrorCodeDescription(fulfillmentSelectionQuery.error)}
            />
          </div>
        </div>
      </>
    );
  }

  return <DealsLoader showBackButton showCloseButton handleBack={onBack} handleClose={onClose} />;
};
